import { useCallback, useEffect, useState } from "react";
import { Button } from "../../ui";
import { formatSar } from "../../../utils/billingMoney";
import { cancelAiWalletTopUpAPI, getAiWalletAPI, getAiWalletTopUpAPI, listAiWalletEntriesAPI, listAiWalletTopUpsAPI, listAiWalletUsageAPI } from "../../../utils/api/aiWallet";
import AiWalletTopUpModal from "./AiWalletTopUpModal";
import { STATE_BADGE, fill, formatWalletDate, useAiWalletText } from "./aiWalletText";
import "./AiWallet.css";

const PAGE = 8;

// The AI Wallet section of the restaurant billing page. It is its own block,
// next to (never mixed with) the subscription: balance, add funds, the
// top-up in progress, what AI usage cost and the wallet's transactions.
// Amounts only - nothing about providers, models, tokens or pricing rules.
//
//   onUploadReceipt(invoice)  opens the page's existing receipt upload
//   reloadKey                 changes when the page refreshed (e.g. after an upload)
//   onChanged()               a top-up was created or cancelled (invoices changed)
const AiWalletPanel = ({ storeId, lang = "ar", reloadKey = 0, onUploadReceipt, onChanged, readOnly = false }) => {
  const text = useAiWalletText(lang);
  const [overview, setOverview] = useState(null);
  const [usage, setUsage] = useState([]);
  const [entries, setEntries] = useState({ list: [], total: 0, limit: PAGE });
  const [topUps, setTopUps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [unavailable, setUnavailable] = useState(false);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(
    async (limit = PAGE) => {
      if (!storeId) return;
      setError("");
      try {
        const data = await getAiWalletAPI(storeId);
        // read-only preview (ceo/cto "View as Store"): drop every write capability
        setOverview(readOnly ? { ...data, permissions: { canViewUsage: data.permissions?.canViewUsage } } : data);
        setUnavailable(false);
        const [entryResult, usageResult, topUpResult] = await Promise.all([
          listAiWalletEntriesAPI(storeId, { limit }),
          data.permissions?.canViewUsage ? listAiWalletUsageAPI(storeId, { limit: PAGE }) : Promise.resolve({ usage: [] }),
          listAiWalletTopUpsAPI(storeId, { limit: PAGE }),
        ]);
        setEntries({ list: entryResult.entries || [], total: entryResult.pagination?.total || 0, limit });
        setUsage(usageResult.usage || []);
        setTopUps(topUpResult || []);
      } catch (err) {
        // No wallet access for this account (or restaurant): the billing page
        // simply has no wallet section.
        if (err.status === 403 || err.status === 404 || err.code === "BILLING_ACCESS_DENIED") setUnavailable(true);
        else setError(err.message);
      } finally {
        setLoading(false);
      }
    },
    [storeId, readOnly],
  );

  useEffect(() => {
    setLoading(true);
    load();
  }, [load, reloadKey]);

  const uploadReceipt = async (topUp) => {
    if (readOnly) return;
    setBusy(true);
    setError("");
    try {
      const detail = await getAiWalletTopUpAPI(topUp._id);
      onUploadReceipt?.({ ...detail.invoice, _id: topUp.invoiceId });
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const cancelTopUp = async (topUp) => {
    if (readOnly) return;
    if (!window.confirm(text.cancelConfirm)) return;
    setBusy(true);
    setError("");
    try {
      await cancelAiWalletTopUpAPI(topUp._id);
      await load();
      await onChanged?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  // Arriving from an AI feature ("Recharge AI Wallet" ->
  // /auth/billing?storeId=..&aiWallet=recharge#billingPanelAiWallet): scroll to
  // this section and, when the user may add funds and no top-up is already
  // waiting, open the top-up form once. The request is then removed from the
  // URL so a reload does not open it again.
  const ready = Boolean(overview);
  useEffect(() => {
    if (!ready) return;
    if (window.location.hash === "#billingPanelAiWallet") document.getElementById("billingPanelAiWallet")?.scrollIntoView?.({ block: "start" });
    const params = new URLSearchParams(window.location.search);
    if (params.get("aiWallet") !== "recharge") return;
    if (overview.permissions?.canRequestTopUp && !overview.openTopUp && overview.wallet?.status !== "frozen") setAdding(true);
    params.delete("aiWallet");
    const search = params.toString();
    window.history.replaceState(window.history.state, "", `${window.location.pathname}${search ? `?${search}` : ""}${window.location.hash}`);
    // Only the first load of the section (the overview object changes on every reload).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  if (unavailable || !storeId) return null;

  const wallet = overview?.wallet;
  const policy = overview?.topUpPolicy;
  const open = overview?.openTopUp;
  const permissions = overview?.permissions || {};
  const signed = (entry) => `${entry.signedMinor >= 0 ? "+" : "−"}${formatSar(Math.abs(entry.signedMinor), lang)}`;

  return (
    <section className="cb-section aiw-section" id="billingPanelAiWallet">
      <div>
        <h2 className="cb-section-title">{text.sectionTitle}</h2>
        <p className="aiw-section-note">{text.sectionNote}</p>
      </div>
      {error && (
        <div className="cb-error">
          {error}{" "}
          <Button size="sm" appearance="outline" onClick={() => load()}>
            {text.retry}
          </Button>
        </div>
      )}
      {loading && !overview ? (
        <div className="cb-loading">{text.loading}</div>
      ) : (
        wallet && (
          <>
            <div className="aiw-top">
              <div className="aiw-balance-card">
                <span className="aiw-label">{text.balance}</span>
                <strong className="aiw-balance" data-testid="ai-wallet-balance">
                  {formatSar(wallet.balanceMinor, lang)}
                </strong>
                <div className="aiw-badges">
                  {wallet.status === "frozen" && <span className="badge-danger">{text.frozen}</span>}
                  {wallet.lowBalance && wallet.status !== "frozen" && <span className="badge-warning">{text.lowBalance}</span>}
                </div>
                {wallet.reservedMinor > 0 && (
                  <span className="aiw-muted">
                    {text.reserved}: {formatSar(wallet.reservedMinor, lang)}
                  </span>
                )}
                {wallet.status === "frozen" && <span className="aiw-muted">{text.frozenNote}</span>}
                {!overview.charging && <span className="aiw-muted">{text.notCharging}</span>}
                <div className="aiw-balance-actions">
                  {permissions.canRequestTopUp && (
                    <Button appearance="filled" disabled={Boolean(open)} onClick={() => setAdding(true)}>
                      {text.addFunds}
                    </Button>
                  )}
                  <span className="aiw-muted">{fill(text.minTopUp, { amount: formatSar(policy.minTopUpMinor, lang) })}</span>
                </div>
              </div>

              {open && (
                <div className="aiw-open-topup" data-testid="ai-wallet-open-top-up">
                  <div className="aiw-open-head">
                    <strong>{text.openTopUpTitle}</strong>
                    <span className={STATE_BADGE[open.state] || "badge-neutral"}>{text.states[open.state] || open.state}</span>
                  </div>
                  <dl className="aiw-facts">
                    <div>
                      <dt>{text.credit}</dt>
                      <dd>{formatSar(open.creditMinor, lang)}</dd>
                    </div>
                    <div>
                      <dt>{text.toPay}</dt>
                      <dd>{formatSar(open.payableMinor, lang)}</dd>
                    </div>
                    <div>
                      <dt>{text.invoice}</dt>
                      <dd>{open.invoiceNumber || "-"}</dd>
                    </div>
                    <div>
                      <dt>{text.expires}</dt>
                      <dd>{formatWalletDate(open.expiresAt, lang)}</dd>
                    </div>
                  </dl>
                  {text.stateNotes[open.state] && <p className="aiw-muted">{text.stateNotes[open.state]}</p>}
                  <div className="aiw-balance-actions">
                    {open.state === "awaiting_payment" && permissions.canUploadReceipt && (
                      <Button appearance="filled" disabled={busy} onClick={() => uploadReceipt(open)}>
                        {text.uploadReceipt}
                      </Button>
                    )}
                    {open.state === "awaiting_payment" && permissions.canRequestTopUp && (
                      <Button appearance="ghost" disabled={busy} onClick={() => cancelTopUp(open)}>
                        {text.cancelRequest}
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="aiw-columns">
              {permissions.canViewUsage && (
                <div className="aiw-block">
                  <h3>{text.usageTitle}</h3>
                  {!usage.length ? (
                    <div className="cb-invoice-empty">{text.usageEmpty}</div>
                  ) : (
                    <div className="cb-table-scroll">
                      <table className="cb-invoice-table">
                        <thead>
                          <tr>
                            <th>{text.colDate}</th>
                            <th>{text.colOperation}</th>
                            <th>{text.colBy}</th>
                            <th>{text.colAmount}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {usage.map((row) => (
                            <tr key={row._id}>
                              <td>{formatWalletDate(row.at, lang)}</td>
                              <td>{text.features[row.feature] || row.feature}</td>
                              <td>{row.by || "-"}</td>
                              <td>
                                {formatSar(row.amountMinor, lang)}
                                {row.refundedMinor > 0 && <span className="badge-neutral aiw-inline-badge">{fill(text.refunded, { amount: formatSar(row.refundedMinor, lang) })}</span>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              <div className="aiw-block">
                <h3>{text.topUpsTitle}</h3>
                {!topUps.length ? (
                  <div className="cb-invoice-empty">{text.empty}</div>
                ) : (
                  <div className="cb-table-scroll">
                    <table className="cb-invoice-table">
                      <thead>
                        <tr>
                          <th>{text.colDate}</th>
                          <th>{text.credit}</th>
                          <th>{text.toPay}</th>
                          <th>{text.colStatus}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topUps.map((topUp) => (
                          <tr key={topUp._id}>
                            <td>{formatWalletDate(topUp.createdAt, lang)}</td>
                            <td>{formatSar(topUp.creditMinor, lang)}</td>
                            <td>{formatSar(topUp.payableMinor, lang)}</td>
                            <td>
                              <span className={STATE_BADGE[topUp.state] || "badge-neutral"}>{text.states[topUp.state] || topUp.state}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            <div className="aiw-block">
              <h3>{text.transactionsTitle}</h3>
              {!entries.list.length ? (
                <div className="cb-invoice-empty">{text.transactionsEmpty}</div>
              ) : (
                <div className="cb-table-scroll">
                  <table className="cb-invoice-table">
                    <thead>
                      <tr>
                        <th>{text.colDate}</th>
                        <th>{text.colType}</th>
                        <th>{text.colAmount}</th>
                        <th>{text.colBalanceAfter}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {entries.list.map((entry) => (
                        <tr key={entry._id}>
                          <td>{formatWalletDate(entry.at, lang)}</td>
                          <td>
                            {text.entryTypes[entry.type] || entry.type}
                            {entry.type === "AI_USAGE" && entry.feature ? ` · ${text.features[entry.feature] || entry.feature}` : ""}
                          </td>
                          <td className={entry.signedMinor >= 0 ? "aiw-credit" : "aiw-debit"} dir="ltr">
                            {signed(entry)}
                          </td>
                          <td>{formatSar(entry.balanceAfterMinor, lang)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {entries.total > entries.list.length && (
                <Button size="sm" appearance="outline" onClick={() => load(entries.limit + 20)}>
                  {text.showMore}
                </Button>
              )}
            </div>
          </>
        )
      )}

      {adding && (
        <AiWalletTopUpModal
          storeId={storeId}
          policy={policy}
          lang={lang}
          text={text}
          onCancel={() => setAdding(false)}
          onDone={async () => {
            setAdding(false);
            await load();
            await onChanged?.();
          }}
        />
      )}
    </section>
  );
};

export default AiWalletPanel;
